import { useContext, useEffect, useRef, useState } from "react";
import { AppLog, PaymentType, SendTxnWPDto, Txn, TxnHeader, TxnStatus, TxnType } from "../../types"
import { Content } from "../../../_myb/layout/components/content";
import PrintDocument from "./PrintTxn";
import { deleteTxnById, exportEInvoice, fetchContact, getTxn, getTxnLogs, getTxnPublicView, getTxnView, importSignedEInvoice, openPdfInNewTab, publicConfirmPayment, publicInitiatePayment, publicPaymentInfo, publicPaymentStatus, sendTxnWp, upSertTxn, warmupPdfLambda } from "../../api";
import { displayDate, sanitizeRenderedHtml } from "../utils/MiscFunctions";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CustomPrompt, CustomPromptProps, emptyCustomPromptProps } from "../widgets/components/ModalPrompts";
import ButtonWithLoader from "../widgets/components/ButtonWithLoader";
import { getTxnConfig, TxnConfig, TxnDirection } from "./TxnConfig";
import { CustomLoader } from "../utils/CustomLoader";
import CustomModal from "../widgets/components/CustomModal";
import { QuickPayForm } from "./PaymentsForm";
import { defaultToolbarState, ToolbarContext } from "../../../_myb/layout/components/toolbar/Toolbar";
import { useAppContext } from "../apps/AppContext";
import { TxnPaymentsList } from "./TxnPayments";
import { DocumentTrail } from "./DocumentTrail";
import { ButtonGroup, Dropdown } from "react-bootstrap";
//Check

// ── TxnLogsPanel — side widget showing notification/send activity ─────────────
function TxnLogsPanel({ logs, onClose }: { logs: AppLog[]; onClose: () => void }) {

    const channelIcon = (subType: string, success: boolean) => {
        const color = success ? 'success' : 'danger';
        let icon = 'bi-activity';
        if (subType === 'WhatsApp') icon = 'bi-whatsapp';
        else if (subType === 'Email') icon = 'bi-envelope';
        else if (subType === 'Created') icon = 'bi-plus-circle';
        else if (subType === 'Updated') icon = 'bi-pencil-square';
        else if (subType === 'Deleted') icon = 'bi-trash';
        else if (subType === 'LateFeePosted') icon = 'bi-clock';
        else if (subType === 'LateFeeReversed') icon = 'bi-arrow-counterclockwise';
        const iconColor = (subType === 'Created' || subType === 'Updated') ? 'primary' : color;
        return (
            <div className="symbol symbol-32px flex-shrink-0">
                <span className={`symbol-label bg-light-${iconColor}`}>
                    <i className={`bi ${icon} fs-6 text-${iconColor}`} />
                </span>
            </div>
        );
    };

    return (
        <div className="card card-bordered shadow-sm" style={{ width: 300, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 20 }}>
            <div className="card-header min-h-50px px-4">
                <h6 className="card-title mb-0 fw-bold text-gray-800">
                    <i className="bi bi-activity me-2 text-primary" />
                    {'Activity Log'.toTranslatedDisplay()}
                </h6>
                <div className="card-toolbar">
                    <button className="btn btn-sm btn-icon btn-light btn-active-light-danger" onClick={onClose} title={'Close'.toTranslatedDisplay()}>
                        <i className="bi bi-x fs-4" />
                    </button>
                </div>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 560, overflowY: 'auto' }}>
                {logs.length === 0 ? (
                    <div className="d-flex flex-column align-items-center py-10 text-center px-4">
                        <i className="bi bi-inbox fs-2x text-muted mb-3" />
                        <span className="text-muted fs-7">{'No activity logged yet'.toTranslatedDisplay()}</span>
                    </div>
                ) : (
                    logs.map((log, idx) => (
                        <div
                            key={log.logId || idx}
                            className={`d-flex align-items-start gap-3 px-4 py-3${idx < logs.length - 1 ? ' border-bottom' : ''}`}
                        >
                            {channelIcon(log.logSubType, log.isSuccess)}
                            <div className="flex-grow-1 min-w-0">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <span className="badge badge-light-secondary fs-8 fw-semibold">{log.logSubType}</span>
                                    {log.logType === 'UserAction' || log.logType === 'SystemAction'
                                        ? null
                                        : log.isSuccess
                                            ? <span className="badge badge-light-success fs-8">{'Sent'.toTranslatedDisplay()}</span>
                                            : <span className="badge badge-light-danger fs-8">{'Failed'.toTranslatedDisplay()}</span>
                                    }
                                </div>
                                <div className="fs-7 text-gray-700 text-break">{log.logSummary}</div>
                                <div className="text-muted fs-8 mt-1">
                                    {log.createdAt ? displayDate(new Date(log.createdAt)) : ''}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

interface ViewTxnProps {
    publicView?: boolean
}

type ViewTxnState = {
    toSendWP?: boolean; // Optional property
};

//TODO Customer/Vendor Payment List
export const ViewTxn: React.FC<ViewTxnProps> = ({ publicView }) => {
    const navigate = useNavigate();
    const { txnType, txnId } = useParams<{ txnType: string; txnId: string }>();
    const [txn, setTxn] = useState<Txn>();
    const [htmlContent, setHtmlContent] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [downloadingPackingSlip, setDownloadingPackingSlip] = useState(false);
    const [packingSlipMode, setPackingSlipMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isPromptVisible, setPromptVisible] = useState(false);
    const [isCancelPromptVisible, setCancelPromptVisible] = useState(false);
    const [customPromptValues, setCustomPromptValues] = useState<CustomPromptProps>(emptyCustomPromptProps)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
    const [isSendingWP, setIsSendingWP] = useState(false);
    const [txnConfig, setTxnConfig] = useState<TxnConfig | null>(null);
    const { updateToolbar } = useContext(ToolbarContext);
    const { bizMaster, bizSettings } = useAppContext();
    const location = useLocation();
    const { toSendWP = false } = (location.state as ViewTxnState) || {};
    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneError, setPhoneError] = useState('');

    const [showPaymentsList, setShowPaymentsList] = useState(false);
    const [txnLogs, setTxnLogs] = useState<AppLog[]>([]);
    const [showLogs, setShowLogs] = useState(false);
    const [eInvoiceUploading, setEInvoiceUploading] = useState(false);
    const [eInvoiceExporting, setEInvoiceExporting] = useState(false);
    const [eInvoiceValidationErrors, setEInvoiceValidationErrors] = useState<string[]>([]);

    // Pay Now panel state (public view only)
    const [gatewayInfo, setGatewayInfo] = useState<{ hasGateway: boolean; balanceDue?: number; currency?: string } | null>(null);
    const [payAmount, setPayAmount] = useState('');
    const [payProcessing, setPayProcessing] = useState(false);
    const [paySuccess, setPaySuccess] = useState(false);
    const [payError, setPayError] = useState('');
    const razorpayRef = useRef<any>(null);
    // Stripe Connect: the Payment Element must be created and mounted into the DOM before
    // stripe.confirmPayment() can be called (MBAI-177 — the previous code called confirmPayment
    // immediately with no mounted Element, which always threw "elements should have a mounted
    // Payment Element"). These refs hold the live Stripe.js objects across the two user actions
    // (init → mount, then Submit Payment → confirm); stripeReady toggles the mount container
    // visible and swaps the amount input for the card form + submit button.
    const [stripeReady, setStripeReady] = useState(false);
    const stripeRef = useRef<any>(null);
    const stripeElementsRef = useRef<any>(null);
    const stripePaymentElementRef = useRef<any>(null);
    const stripeElementMountRef = useRef<HTMLDivElement>(null);
    const stripeLogIdRef = useRef<string>('');
    const updateButtons = () => {
        updateToolbar({
            primaryButton: {
                enabled: true,
                text: `${'Create'.toTranslatedDisplay()} ${txnConfig?.title?.toTranslatedDisplay() || ''}`,
                shortcutKeys: ['ctrl+g', 'meta+g'],
                shortcutTip: 'Ctrl+g / ⌘+g',
                icon: 'create', // Replace with appropriate icon type
                action: async () => {
                    navigate(`/transactions/${txnConfig?.createRoute}`);
                    setIsModalOpen(true);
                }
            },
            secondaryButton: {
                enabled: true,
                text: `Configure Print Template`,
                // className:'btn-light-primary',
                icon: 'create', // Replace with appropriate icon type
                action: async () => {
                    navigate(`/settings/print-templates`);
                    setIsModalOpen(true);
                }
            },
        });
    };
    // Bumped after an allocation is deleted from this return so the rendered document/balance refreshes
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        updateButtons();
    }, [txnConfig])
    useEffect(() => {
        // txnType can also be a PaymentType (e.g. "CustomerPayment") when this is a
        // shared public link to a payment receipt rather than a transaction —
        // getTxnConfig only knows TxnType values, so skip it in that case instead of throwing.
        const isKnownTxnType = !!txnType && (Object.values(TxnType) as string[]).includes(txnType);
        const config = isKnownTxnType ? getTxnConfig(txnType as TxnType) : null;
        setTxnConfig(config);
        const defaultPackingSlip = config?.defaultPackingSlip === true;
        setPackingSlipMode(defaultPackingSlip);
        // Pre-warm the PDF Lambda so it's hot by the time the user clicks Download
        if (!publicView) warmupPdfLambda();
        const fetchTxnView = async () => {
            if (txnId) {
                setLoading(true)
                const apiResult = await getTxnPublicView(txnId, defaultPackingSlip)
                const sanitizedHtml = sanitizeRenderedHtml(apiResult.renderTxn);
                setHtmlContent(sanitizedHtml)
                setLoading(false)
                setTxn(apiResult.txn)
                if (toSendWP && apiResult.txn && config) {
                    sendWhatsApp(apiResult.renderTxn, apiResult.txn, config)
                }
            }
        }
        fetchTxnView()

        // Fetch activity logs; auto-show panel if any exist
        if (txnId && !publicView) {
            getTxnLogs(txnId)
                .then(logs => {
                    setTxnLogs(logs);
                    if (logs.length > 0) setShowLogs(true);
                })
                .catch(() => {}); // silently ignore — logs are non-critical
        }

        // Load gateway info for Pay Now panel (public view, Invoice only)
        if (txnId && publicView && txnType === TxnType.Invoice) {
            publicPaymentInfo(txnId)
                .then(info => {
                    setGatewayInfo(info);
                    if (info.balanceDue) setPayAmount(String(info.balanceDue));
                })
                .catch(() => {});
        }
    }, [txnId, isModalOpen, reloadKey])
    const handlePayNow = async () => {
        if (!txnId || !gatewayInfo?.hasGateway) return;
        const amount = parseFloat(payAmount);
        if (!amount || amount <= 0) { setPayError('Enter a valid amount'); return; }
        if (amount > (gatewayInfo.balanceDue || 0) + 0.01) { setPayError(`Amount cannot exceed ${gatewayInfo.currency} ${gatewayInfo.balanceDue}`); return; }

        setPayError('');
        setPayProcessing(true);
        try {
            const initResult = await publicInitiatePayment(txnId, amount);

            if (initResult.gateway === 'razorpay') {
                // Previously: no onerror handler, and the Razorpay constructor/.open() call sat
                // outside any try/catch — a failed script load (network blip, ad-blocker) or a
                // synchronous throw from `new Razorpay(...)`/`.open()` left payProcessing stuck
                // true forever with no error shown, permanently disabling the Pay Now button
                // (mirrors the same class of bug fixed for Stripe above). Also skip re-injecting
                // the script tag on a retry, matching the Stripe branch's existing pattern.
                const loadRazorpayJs = (): Promise<void> => new Promise((resolve, reject) => {
                    if ((window as any).Razorpay) { resolve(); return; }
                    const script = document.createElement('script');
                    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error('Failed to load Razorpay checkout script'));
                    document.body.appendChild(script);
                });
                try {
                    await loadRazorpayJs();
                    const options = {
                        key: initResult.keyId,
                        order_id: initResult.orderId,
                        amount: Math.round(amount * 100),
                        currency: gatewayInfo.currency || 'INR',
                        name: 'Invoice Payment',
                        handler: async (response: any) => {
                            try {
                                const confirm = await publicConfirmPayment(initResult.logId, response.razorpay_payment_id, response.razorpay_signature, response.razorpay_order_id);
                                if (confirm.success) {
                                    setPaySuccess(true);
                                    setGatewayInfo(g => g ? { ...g, balanceDue: confirm.newBalance } : g);
                                } else {
                                    setPayError('Payment verification failed. Contact the merchant.');
                                }
                            } catch { setPayError('Payment verification failed.'); }
                        },
                        modal: { ondismiss: () => setPayProcessing(false) },
                    };
                    razorpayRef.current = new (window as any).Razorpay(options);
                    razorpayRef.current.open();
                } catch {
                    setPayError('Razorpay checkout could not be loaded. Please try again.');
                } finally {
                    setPayProcessing(false);
                }

            } else if (initResult.gateway === 'phonepe' && initResult.redirectUrl) {
                // Store logId in sessionStorage so we can poll on return
                sessionStorage.setItem('pg_logId', initResult.logId);
                window.location.href = initResult.redirectUrl;

            } else if (initResult.gateway === 'stripe_connect' && initResult.clientSecret) {
                // Stripe.js — loaded at runtime via CDN to avoid package dependency
                const loadStripeJs = (): Promise<any> => new Promise((resolve, reject) => {
                    if ((window as any).Stripe) { resolve((window as any).Stripe); return; }
                    const s = document.createElement('script');
                    s.src = 'https://js.stripe.com/v3/';
                    s.onload = () => resolve((window as any).Stripe);
                    s.onerror = reject;
                    document.body.appendChild(s);
                });
                try {
                    const StripeJs = await loadStripeJs();
                    // The PaymentIntent behind clientSecret was created on the business's own
                    // connected account ({ stripeAccount: accountId } on the backend) — Stripe.js
                    // must be scoped to that same account or confirmPayment() fails with a
                    // "No such payment_intent" style error (it looks on the platform account
                    // by default). initResult.accountId comes from the /payments/public/initiate
                    // response (MBAI-177).
                    const stripe = StripeJs(import.meta.env.VITE_APP_STRIPE_KEY || '', { stripeAccount: initResult.accountId });
                    const elements = stripe.elements({ clientSecret: initResult.clientSecret });
                    const paymentElement = elements.create('payment');
                    // The mount container is always in the DOM (hidden via CSS until stripeReady),
                    // so the ref is already populated here — no extra render/effect round trip needed.
                    if (stripeElementMountRef.current) paymentElement.mount(stripeElementMountRef.current);
                    stripeRef.current = stripe;
                    stripeElementsRef.current = elements;
                    stripePaymentElementRef.current = paymentElement;
                    stripeLogIdRef.current = initResult.logId;
                    // Survives a real browser redirect (3-D Secure, redirect-only payment methods) —
                    // picked back up by the useEffect below, mirroring the existing PhonePe pattern.
                    sessionStorage.setItem('pg_stripe_logId', initResult.logId);
                    setStripeReady(true);
                } catch {
                    setPayError('Stripe payment could not be initialized. Please contact the merchant.');
                }
                setPayProcessing(false);
            }
        } catch (err: any) {
            setPayError(err?.response?.data?.message || err.message || 'Payment initiation failed');
            setPayProcessing(false);
        }
    };

    // Fires when the customer submits the mounted Stripe Payment Element (MBAI-177).
    const handleStripeSubmit = async () => {
        if (!stripeRef.current || !stripeElementsRef.current) return;
        setPayError('');
        setPayProcessing(true);
        try {
            // redirect:'if_required' keeps the customer on this page for the common case (a card
            // that doesn't need 3-D Secure); only a payment method that genuinely requires leaving
            // the page (3DS challenge, redirect-only methods) navigates away, in which case the
            // useEffect below picks the result back up on return via sessionStorage.
            const { error, paymentIntent } = await stripeRef.current.confirmPayment({
                elements: stripeElementsRef.current,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
            });
            if (error) {
                setPayError(error.message || 'Payment failed');
                return;
            }
            if (paymentIntent?.status === 'succeeded') {
                const confirm = await publicConfirmPayment(stripeLogIdRef.current, paymentIntent.id);
                sessionStorage.removeItem('pg_stripe_logId');
                if (confirm.success) {
                    setPaySuccess(true);
                    setGatewayInfo(g => g ? { ...g, balanceDue: confirm.newBalance } : g);
                } else {
                    setPayError('Payment verification failed. Contact the merchant.');
                }
            } else {
                setPayError('Payment was not completed. Please try again.');
            }
        } catch {
            setPayError('Payment could not be processed. Please try again.');
        } finally {
            setPayProcessing(false);
        }
    };

    // Poll for PhonePe result on return from redirect
    useEffect(() => {
        const logId = sessionStorage.getItem('pg_logId');
        if (!logId || !publicView) return;
        sessionStorage.removeItem('pg_logId');
        publicPaymentStatus(logId).then(s => {
            if (s.status === 'success') setPaySuccess(true);
            else if (s.status === 'failed') setPayError('Payment was not completed. Please try again.');
        }).catch(() => {});
    }, [publicView]);

    // Pick up a Stripe payment that genuinely redirected away and back (3-D Secure, a
    // redirect-only payment method) — the no-redirect case is handled inline in
    // handleStripeSubmit (MBAI-177). Stripe appends payment_intent/redirect_status itself.
    useEffect(() => {
        if (!publicView) return;
        const params = new URLSearchParams(window.location.search);
        const paymentIntentId = params.get('payment_intent');
        const redirectStatus = params.get('redirect_status');
        const stripeLogId = sessionStorage.getItem('pg_stripe_logId');
        if (!paymentIntentId || !redirectStatus || !stripeLogId) return;

        window.history.replaceState({}, '', window.location.pathname);
        sessionStorage.removeItem('pg_stripe_logId');

        if (redirectStatus !== 'succeeded') {
            setPayError('Payment was not completed. Please try again.');
            return;
        }
        publicConfirmPayment(stripeLogId, paymentIntentId)
            .then(confirm => {
                if (confirm.success) {
                    setPaySuccess(true);
                    setGatewayInfo(g => g ? { ...g, balanceDue: confirm.newBalance } : g);
                } else {
                    setPayError('Payment verification failed. Contact the merchant.');
                }
            })
            .catch(() => setPayError('Payment verification failed. Contact the merchant.'));
    }, [publicView]);

    const handleDownload = async () => {
        setDownloading(true)
        if (txn) await openPdfInNewTab(txn.txnHeader.txnId, txn.txnHeader.txnNo || '', packingSlipMode);
        setDownloading(false)
    };
    // Downloads/prints the alternate template — Packing Slip when the main view is the
    // regular document, or the regular document (e.g. Delivery Note) when the main view
    // defaults to Packing Slip (MBAI-143).
    const handlePackingSlip = async () => {
        setDownloadingPackingSlip(true)
        if (txn) await openPdfInNewTab(txn.txnHeader.txnId, txn.txnHeader.txnNo || '', !packingSlipMode);
        setDownloadingPackingSlip(false)
    };
    const handlePrintPackingSlip = async () => {
        setDownloadingPackingSlip(true);
        try {
            if (txn) {
                const altHtml = await getTxnView(txn.txnHeader.txnId, !packingSlipMode);
                await handlePrint(altHtml);
            }
        } catch (error) {
            console.error('Error printing document:', error);
            toast.error('Failed to print document'.toTranslatedDisplay());
        }
        setDownloadingPackingSlip(false);
    };

    const handleConvert = (targetType?: TxnType) => {
        const defaultTargetType = (txnConfig?.txnDirection === TxnDirection.Inward) ? TxnType.Invoice : TxnType.PurchaseBills;
        const targetTxnType = targetType || defaultTargetType;
        const targetTxnConfig = getTxnConfig(targetTxnType);
        const copyTxn = txn;
        if (copyTxn?.txnHeader) {
            // MBAI-324: store source txn type so TxnForm can render a back-link
            copyTxn.txnHeader.convertedFromTxnId = txn?.txnHeader.txnId;
            copyTxn.txnHeader.convertedFromTxnType = txn?.txnHeader.txnType;
            copyTxn.txnHeader.txnType = targetTxnType;
        }
        navigate(`/transactions/${targetTxnConfig?.createRoute}`, { state: copyTxn });
    }

    const handleStatusUpdate = async (txnStatus: TxnStatus) => {
        try {
            if (txn) {
                txn.txnHeader.txnStatus = txnStatus;
                const result = await upSertTxn(txn);
                if (result) {
                    toast.success(`${txnType?.toTranslatedDisplay()} ${'Status Updated!'.toTranslatedDisplay()}`);
                } else {
                    toast.error(`${'Error updating status for'.toTranslatedDisplay()} ${txnType?.toTranslatedDisplay()}`);
                }
            } else {
                toast.error('Transaction data is missing.'.toTranslatedDisplay());
            }
        } catch (error) {
            console.error('Error updating transaction status:', error);
            toast.error(`${'An unexpected error occurred while updating status for'.toTranslatedDisplay()} ${txnType?.toTranslatedDisplay()}`);
        }
    };

    const handleSendWP = async () => {
        if (txn && txnConfig) sendWhatsApp(htmlContent, txn, txnConfig)
    }

    const sendWhatsApp = async (txnHtml: string, txn: Txn, txnConfigForWP: TxnConfig) => {
        try {
            const { contact } = await fetchContact(txn.txnHeader.contactId || '');
            const existingPhone = contact?.workPhone ? contact.workPhone.toString() : '';

            // If no phone number exists, show modal to capture it
            if (!existingPhone) {
                setIsPhoneModalOpen(true);
                setPhoneNumber('');
                setPhoneError('');
                return;
            }

            // Proceed with sending if phone exists
            await sendWhatsAppMessage(txnHtml, txn, txnConfigForWP, [existingPhone]);
        } catch (error) {
            console.error('Error fetching contact:', error);
            toast.error('Failed to fetch contact details'.toTranslatedDisplay());
        }
    };

    const sendWhatsAppMessage = async (txnHtml: string, txn: Txn, txnConfigForWP: TxnConfig, phoneNumbers: string[]) => {
        const toastId = toast.info('Sending WhatsApp message...'.toTranslatedDisplay(), {
            autoClose: false,
        });

        try {
            const { contact } = await fetchContact(txn.txnHeader.contactId || '');

            const sendTxnWpDto = new SendTxnWPDto({
                htmlContent: txnHtml,
                txnId: txn.txnHeader.txnId,
                txnType: txn.txnHeader.txnType,
                txnNo: txn.txnHeader.txnNo,
                amount: txn.txnHeader.totalAmt || 0,
                customerName: contact?.contactName || 'Customer',
                businessName: bizMaster?.bizDetails?.bizName || 'Business',
                currencyId: bizMaster?.bizDetails?.currency,
                country: bizMaster?.bizDetails?.country,
                phoneNumber: phoneNumbers,
            });

            const sendWpResponse = await sendTxnWp(sendTxnWpDto);

            if (sendWpResponse) {
                toast.update(toastId, {
                    render: `${txnConfigForWP?.title} sent successfully!`,
                    type: 'success',
                    autoClose: 3000,
                });
            } else {
                throw new Error('Failed to send WhatsApp message');
            }
        } catch (error) {
            console.error('Error sending WhatsApp message:', error);
            toast.update(toastId, {
                render: 'Failed to send WhatsApp message. Please try again.',
                type: 'error',
                autoClose: 5000,
            });
        }
    };

    const validatePhoneNumber = (phone: string): boolean => {
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');

        // Check if it's between 10-15 digits (standard international phone number length)
        if (cleaned.length < 10 || cleaned.length > 15) {
            setPhoneError('Phone number must be between 10-15 digits');
            return false;
        }

        setPhoneError('');
        return true;
    };

    const handlePhoneSubmit = async () => {
        if (!validatePhoneNumber(phoneNumber)) {
            return;
        }

        setIsPhoneModalOpen(false);

        if (txn && txnConfig) {
            await sendWhatsAppMessage(htmlContent, txn, txnConfig, [phoneNumber.replace(/\D/g, '')]);
        }
    };

    const handlePhoneModalClose = () => {
        setIsPhoneModalOpen(false);
        setPhoneNumber('');
        setPhoneError('');
    };
    const handleCopy = () => {
        navigate(`/transactions/${txnConfig?.createRoute}`, { state: txn });
    }

    const handleShare = () => {
        const baseurl = window.location.origin; // Access the environment variable

        const url = `${baseurl}/txn/${txnType}/${txnId}`;
        setPromptVisible(true)
        customPromptValues.secondaryText = 'Close'.toTranslatedDisplay()
        customPromptValues.onConfirm = handleClose
        customPromptValues.title = 'Share'.toTranslatedDisplay()
        customPromptValues.message = (
            <>
                <div className="d-flex">
                    <input type="text" className="w-100" value={url} disabled={true} />
                    <div onClick={() => {
                        navigator.clipboard.writeText(url);
                        toast.success('URL copied to clipboard'.toTranslatedDisplay());
                    }}
                        className="cursor-pointer input-group-prepend">
                        <span className="d-inline-block rounded-0 rounded-end input-group-text text-truncate" style={{
                            height: '100%',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                        }}> <i className="bi bi-copy fs-2"></i> {/* Print icon */}</span>
                    </div>
                </div>
            </>

        )
    };

    const handleEditTxn = () => {
        navigate(`/transactions/edit-txn/${txnType}/${txnId}`); // Navigate to the edit transaction page
    };
    const handleClose = () => {
        if (txnConfig?.listRoute) {
            navigate(txnConfig.listRoute);
        } else {
            navigate(-1);
        }
    };
    const confirmDeleteTxn = async () => {
        try {
            if (txnId) {
                const result = await deleteTxnById(txnId);
                toast.success(`${txnType?.toTranslatedDisplay()} ${'deleted!'.toTranslatedDisplay()}`)
                navigate(-1)
            } else {
                toast.error(`${'Failed to delete'.toTranslatedDisplay()} ${txnType?.toTranslatedDisplay()}`)
            }

        } catch (error) {
            // Surface the server's reason (e.g. return still has allocations) instead of a generic failure
            toast.error((error instanceof Error && error.message) || `${'Failed to delete'.toTranslatedDisplay()} ${txnType?.toTranslatedDisplay()}`)
            console.error('Failed to delete transaction:', error);
        }
    }
    const handleSend = () => {
        navigate(`/transactions/send-txn/${txnType}/${txnId}`); // Navigate to the edit transaction page
    }
    const handleDeleteTxn = async () => {
        setPromptVisible(true)
        customPromptValues.onConfirm = confirmDeleteTxn
        customPromptValues.message = 'Do you really want to delete?'.toTranslatedDisplay()
        customPromptValues.title = 'Confirm Delete'.toTranslatedDisplay()
        customPromptValues.PrimaryText = 'Delete'.toTranslatedDisplay()
        customPromptValues.secondaryText = 'Cancel'.toTranslatedDisplay()
    };

    const confirmCancelTxn = async () => {
        try {
            if (txn) {
                const txnForCancel = { ...txn }; // Create a proper copy to avoid mutation
                txnForCancel.txnHeader.txnStatus = TxnStatus.Cancelled;
                setTxn(txnForCancel);

                const uploadStatus = await upSertTxn(txnForCancel);
                toast.success(`${txnType?.toTranslatedDisplay()} ${'Cancelled'.toTranslatedDisplay()}`);
                navigate(-1);
            } else {
                toast.error(`${'Failed to cancel'.toTranslatedDisplay()} ${txnType?.toTranslatedDisplay()}`);
            }
        } catch (error: any) {
            console.error('Failed to cancel transaction:', error);
            // Since handleApiError already formats the error message, just use error.message
            toast.error(error.message || `${'Failed to cancel'.toTranslatedDisplay()} ${txnType?.toTranslatedDisplay()}`);
        }
    }

    const handleCancelTxn = async () => {
        setPromptVisible(true)
        customPromptValues.onConfirm = confirmCancelTxn
        customPromptValues.message = 'Do you really want to Cancel/Void?'.toTranslatedDisplay()
        customPromptValues.title = 'Confirm Cancel'.toTranslatedDisplay()
        customPromptValues.PrimaryText = 'Void/Cancel'.toTranslatedDisplay()
        customPromptValues.secondaryText = 'Close'.toTranslatedDisplay()
    };

    const handleClosePrompt = () => {
        setPromptVisible(false);
    };

    const handleCloseCancelPrompt = () => {
        setCancelPromptVisible(false);
    };

    const isEInvoiceEligible = !publicView
        && bizSettings?.eInvoiceEnabled === true
        && txnType === TxnType.Invoice;

    const handleEInvoiceExport = async () => {
        setEInvoiceValidationErrors([]);
        setEInvoiceExporting(true);
        try {
            await exportEInvoice(txn!.txnHeader.txnId, txn!.txnHeader.txnNo || txn!.txnHeader.txnId);
        } catch (error: any) {
            const missingFields: string[] = Array.isArray(error?.missingFields) ? error.missingFields : [];
            if (missingFields.length > 0) {
                setEInvoiceValidationErrors(missingFields);
            } else {
                toast.error(error?.message || 'Failed to export e-Invoice JSON');
            }
        } finally {
            setEInvoiceExporting(false);
        }
    };

    const handleEInvoiceImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !txn) return;
        e.target.value = '';
        setEInvoiceUploading(true);
        try {
            const result = await importSignedEInvoice(txn.txnHeader.txnId, file);
            setTxn(prev => prev ? {
                ...prev,
                txnHeader: { ...prev.txnHeader, irn: result.irn, irnAckNo: result.irnAckNo, irnAckDate: result.irnAckDate as any },
            } : prev);
            // Refresh the PDF preview so QR appears
            const refreshed = await getTxnPublicView(txn.txnHeader.txnId);
            setHtmlContent(sanitizeRenderedHtml(refreshed.renderTxn));
            toast.success('Signed JSON imported. IRN generated successfully.'.toTranslatedDisplay());
        } catch (error: any) {
            toast.error(error?.message || 'Failed to import signed JSON'.toTranslatedDisplay());
        } finally {
            setEInvoiceUploading(false);
        }
    };

    if (loading) {
        return <CustomLoader />
    }

    const handleModalClose = () => {
        setIsModalOpen(false);
    };
    return (
        <Content>
            {!publicView && txn && (
                <DocumentTrail txn={txn} txnConfig={txnConfig} />
            )}
            <div className="d-flex justify-content-center mb-3">
                {publicView ? <>
                    <div className="w-100 d-flex justify-content-center me-20">
                        <ButtonWithLoader isLoading={downloading} onClick={handleDownload} className="btn btn-secondary me-2">
                            <i className="bi bi-download fs-2"></i> {'Download'.toTranslatedDisplay()}
                        </ButtonWithLoader>
                    </div>
                </> :
                    <>
                        {txnConfig?.paymentRelated && (txn?.txnHeader?.balanceDue || 0) > 0 &&
                            <button type="button" onClick={() => setIsModalOpen(true)} className='btn btn-sm btn-primary me-2'>{'Record Payment'.toTranslatedDisplay()}</button>
                        }
                        {txnType === TxnType.SalesOrder && (
                            <button
                                type="button"
                                onClick={() => setIsAdvanceModalOpen(true)}
                                className='btn btn-sm btn-light-primary me-2'
                            >
                                <i className="bi bi-cash-coin me-1"></i> {'Receive Advance'.toTranslatedDisplay()}
                            </button>
                        )}
                        {txnConfig?.returnType && (txn?.txnHeader?.balanceDue || 0) > 0 &&
                            <button type="button" onClick={() => navigate(`/transactions/allocate-return-txn/${txnConfig.returnType}/${txnId}`)} className='btn btn-sm btn-primary me-2'>{'Apply Credit'.toTranslatedDisplay()}</button>
                        }
                        {/* Plain button (not ButtonWithLoader): its wrapper span stops the button stretching to the toolbar row height like its siblings */}
                        <button type="button" disabled={downloading} onClick={handleDownload} className="btn btn-sm btn-secondary me-2">
                            {downloading
                                ? <span className="spinner-border spinner-border-sm align-middle me-1"></span>
                                : <i className="bi bi-download me-1"></i>} {'Download PDF'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={handleEditTxn} className="btn btn-sm btn-secondary me-2">
                            <i className="bi bi-pencil me-1"></i> {'Edit'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={() => handlePrint(htmlContent)} className="btn btn-sm btn-secondary me-2">
                            <i className="bi bi-printer me-1"></i> {'Print'.toTranslatedDisplay()}
                        </button>
                        {txnConfig?.showConvert && (
                            <Dropdown as={ButtonGroup} className="me-2">
                                <button
                                    type="button"
                                    onClick={() => handleConvert()}
                                    className="btn btn-sm btn-secondary no-right-rounded"
                                >
                                    <i className="bi bi-arrow-right fs-2 me-1"></i>
                                    {'Convert'.toTranslatedDisplay()}
                                </button>
                                {txnType === TxnType.Estimate && (
                                    <>
                                        <div className="btn-splitter border border-secondary-light"></div>
                                        <Dropdown.Toggle
                                            split
                                            variant="secondary"
                                            id="dropdown-convert"
                                            className="fs-3 btn-sm"
                                        />
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleConvert(TxnType.Invoice)}>
                                                <i className="bi bi-file-earmark-text me-2"></i> {'Convert to Invoice'.toTranslatedDisplay()}
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleConvert(TxnType.ProformaInvoice)}>
                                                <i className="bi bi-file-earmark-ruled me-2"></i> {'Convert to Proforma Invoice'.toTranslatedDisplay()}
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </>
                                )}
                            </Dropdown>
                        )}


                        <button type="button" onClick={handleCopy} className="btn btn-sm btn-secondary me-2">
                            <i className="bi bi-copy me-1"></i> {'Copy'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={handleShare} className="btn btn-sm btn-secondary me-2">
                            <i className="bi bi-share me-1"></i> {'Share Link'.toTranslatedDisplay()}
                        </button>
                        <Dropdown as={ButtonGroup} className="me-2">
                            <ButtonWithLoader
                                isLoading={downloadingPackingSlip}
                                onClick={handlePackingSlip}
                                className="btn btn-secondary no-right-rounded"
                            >
                                {packingSlipMode ? 'Delivery Note'.toTranslatedDisplay() : 'Packing Slip'.toTranslatedDisplay()}
                            </ButtonWithLoader>
                            <div className="btn-splitter border border-secondary-light"></div>
                            <Dropdown.Toggle
                                split
                                variant="secondary"
                                id="dropdown-packing-slip"
                                className="fs-3 btn-sm"
                                disabled={downloadingPackingSlip}
                            />
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handlePackingSlip}>
                                    <i className="bi bi-download me-2"></i> {'Download PDF'.toTranslatedDisplay()}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handlePrintPackingSlip}>
                                    <i className="bi bi-printer me-2"></i> {'Print Directly'.toTranslatedDisplay()}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <button type="button" onClick={handleSend} className="btn btn-sm btn-secondary me-2">
                            <i className="bi bi-send me-1"></i> {'Send Email'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={handleSendWP} className="btn btn-sm btn-secondary me-2" title={'Send via WhatsApp'.toTranslatedDisplay()}>
                            <i className="bi bi-whatsapp me-1"></i> {'WhatsApp'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={handleDeleteTxn} className="btn btn-sm btn-light-danger me-2">
                            <i className="bi bi-trash me-1"></i> {'Delete'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={handleCancelTxn} className="btn btn-sm btn-light-danger me-2">
                            <i className="bi bi-slash-circle me-1"></i> {'Void'.toTranslatedDisplay()}
                        </button>
                        <button type="button" onClick={handleClose} className="btn btn-sm btn-light-warning">
                            <i className="bi bi-x-circle me-1"></i> {'Close'.toTranslatedDisplay()}
                        </button>
                        {isEInvoiceEligible && (
                            <Dropdown as={ButtonGroup} className="ms-2">
                                <ButtonWithLoader
                                    isLoading={eInvoiceExporting}
                                    onClick={handleEInvoiceExport}
                                    className="btn btn-sm btn-light-info no-right-rounded"
                                >
                                    <i className="bi bi-file-earmark-code me-1"></i>
                                    {'e-Invoice'.toTranslatedDisplay()}
                                </ButtonWithLoader>
                                <div className="btn-splitter border border-secondary-light"></div>
                                <Dropdown.Toggle split variant="info" id="dropdown-einvoice" className="btn-sm fs-3 btn-light-info" />
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleEInvoiceExport} disabled={eInvoiceExporting}>
                                        <i className="bi bi-download me-2"></i> {'Export JSON'.toTranslatedDisplay()}
                                    </Dropdown.Item>
                                    <Dropdown.Item as="label" style={{ cursor: 'pointer' }}>
                                        <i className="bi bi-upload me-2"></i> {'Upload Signed JSON'.toTranslatedDisplay()}
                                        {eInvoiceUploading && <span className="spinner-border spinner-border-sm ms-2" />}
                                        <input
                                            type="file"
                                            accept=".json"
                                            className="d-none"
                                            onChange={handleEInvoiceImport}
                                            disabled={eInvoiceUploading}
                                        />
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </>}

            </div>
            {isEInvoiceEligible && eInvoiceValidationErrors.length > 0 && (
                <div className="alert alert-warning d-flex align-items-start gap-3 mt-3">
                    <i className="bi bi-exclamation-triangle-fill fs-4 text-warning mt-1" />
                    <div>
                        <div className="fw-bold mb-1">{'Cannot generate e-Invoice JSON. Missing fields:'.toTranslatedDisplay()}</div>
                        <ul className="mb-0 ps-3">
                            {eInvoiceValidationErrors.map((field, i) => <li key={i}>{field}</li>)}
                        </ul>
                    </div>
                </div>
            )}
            {isEInvoiceEligible && txn?.txnHeader?.irn && (
                <div className="card border border-success-subtle mt-3 mb-2">
                    <div className="card-body py-4 px-5">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <h6 className="card-title mb-0 fw-bold">{'e-Invoice Details'.toTranslatedDisplay()}</h6>
                            <span className="badge badge-light-success fw-semibold">{'IRN Generated'.toTranslatedDisplay()}</span>
                        </div>
                        <div className="mb-2 d-flex align-items-center gap-2">
                            <span className="text-muted fw-semibold me-1">{'IRN:'.toTranslatedDisplay()}</span>
                            <span className="font-monospace" title={txn.txnHeader.irn}>
                                {txn.txnHeader.irn.length > 20 ? txn.txnHeader.irn.slice(0, 20) + '…' : txn.txnHeader.irn}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-icon btn-light"
                                title={'Copy IRN'.toTranslatedDisplay()}
                                onClick={() => { navigator.clipboard.writeText(txn.txnHeader.irn!); toast.info('IRN copied to clipboard'.toTranslatedDisplay()); }}
                            >
                                <i className="bi bi-clipboard fs-6" />
                            </button>
                        </div>
                        {txn.txnHeader.irnAckNo && (
                            <div className="mb-1">
                                <span className="text-muted fw-semibold me-2">{'Ack No:'.toTranslatedDisplay()}</span>
                                <span>{txn.txnHeader.irnAckNo}</span>
                            </div>
                        )}
                        {txn.txnHeader.irnAckDate && (
                            <div className="mb-1">
                                <span className="text-muted fw-semibold me-2">{'Ack Date:'.toTranslatedDisplay()}</span>
                                <span>{displayDate(new Date(txn.txnHeader.irnAckDate))}</span>
                            </div>
                        )}
                        {txn.txnHeader.eInvoiceQr && (
                            <div className="mt-2 text-muted fst-italic fs-7">
                                <i className="bi bi-qr-code me-1" />
                                {'QR code is included in the Tax Invoice PDF'.toTranslatedDisplay()}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <CustomPrompt
                isVisible={isPromptVisible}
                onClose={handleClosePrompt}
                onConfirm={customPromptValues.onConfirm}
                message={customPromptValues.message}
                title={customPromptValues.title}
                PrimaryText={customPromptValues.PrimaryText}
                secondaryText={customPromptValues.secondaryText}
            />
            {(txnConfig?.paymentRelated || txnConfig?.returnType) && txnId && !publicView && (
                <div className="mt-5">
                    <TxnPaymentsList txnId={txnId} isReturn={Boolean(txnConfig?.returnType)} onAllocationDeleted={() => setReloadKey(k => k + 1)} />
                </div>
            )}
            <div className={`d-flex align-items-start gap-4 mt-3`}>
                <div className="flex-grow-1 min-w-0">
                    <PrintDocument templateString={htmlContent} />
                </div>
                {showLogs && !publicView && (
                    <TxnLogsPanel logs={txnLogs} onClose={() => setShowLogs(false)} />
                )}
                {/* Pay Now panel — shown only in public Invoice view when gateway is configured */}
                {publicView && txnType === TxnType.Invoice && gatewayInfo?.hasGateway && (
                    <div className="card shadow-sm" style={{ minWidth: 300, maxWidth: 340, flexShrink: 0 }}>
                        <div className="card-body p-6">
                            {paySuccess ? (
                                <div className="text-center py-4">
                                    <div className="text-success mb-3" style={{ fontSize: 48 }}>&#10003;</div>
                                    <h5 className="fw-bold text-success">{'Payment Successful!'.toTranslatedDisplay()}</h5>
                                    <p className="text-muted fs-7 mt-2">
                                        {gatewayInfo.balanceDue && gatewayInfo.balanceDue > 0
                                            ? `${'Remaining balance:'.toTranslatedDisplay()} ${gatewayInfo.currency} ${gatewayInfo.balanceDue.toFixed(2)}`
                                            : 'Invoice is fully paid.'.toTranslatedDisplay()}
                                    </p>
                                </div>
                            ) : gatewayInfo.balanceDue !== undefined && gatewayInfo.balanceDue <= 0 ? (
                                // Revisiting (or refreshing) an already fully-paid invoice's public link —
                                // distinct from paySuccess, which only covers the payment just made in this
                                // browser session. Without this, the amount-input + Pay Now form kept
                                // showing indefinitely for a $0-balance invoice (MBAI-177).
                                <div className="text-center py-4">
                                    <div className="text-success mb-3" style={{ fontSize: 48 }}>&#10003;</div>
                                    <h5 className="fw-bold text-success">{'Invoice Fully Paid'.toTranslatedDisplay()}</h5>
                                    <p className="text-muted fs-7 mt-2">{'No balance is due on this invoice.'.toTranslatedDisplay()}</p>
                                </div>
                            ) : (
                                <>
                                    <h5 className="fw-bold mb-4">{'Pay Invoice'.toTranslatedDisplay()}</h5>
                                    {gatewayInfo.balanceDue !== undefined && (
                                        <div className="d-flex justify-content-between mb-3">
                                            <span className="text-muted fs-7">{'Balance Due'.toTranslatedDisplay()}</span>
                                            <span className="fw-bold">{gatewayInfo.currency} {gatewayInfo.balanceDue.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {!stripeReady && (
                                        <div className="mb-4">
                                            <label className="form-label fs-7 fw-semibold">{'Amount to Pay'.toTranslatedDisplay()}</label>
                                            <div className="input-group">
                                                <span className="input-group-text">{gatewayInfo.currency || 'INR'}</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={payAmount}
                                                    min={1}
                                                    max={gatewayInfo.balanceDue}
                                                    step={0.01}
                                                    onChange={e => { setPayAmount(e.target.value); setPayError(''); }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {/* Stripe Connect card form — always mounted so the ref exists before Pay Now
                                        runs (MBAI-177); hidden via CSS until the Payment Element is created. */}
                                    <div ref={stripeElementMountRef} className="mb-4" style={{ display: stripeReady ? 'block' : 'none' }} />
                                    {payError && <div className="alert alert-danger py-2 fs-7 mb-3">{payError}</div>}
                                    {stripeReady ? (
                                        <ButtonWithLoader
                                            isLoading={payProcessing}
                                            onClick={handleStripeSubmit}
                                            className="btn btn-success w-100 fw-bold"
                                        >
                                            {'Submit Payment'.toTranslatedDisplay()}
                                        </ButtonWithLoader>
                                    ) : (
                                        <ButtonWithLoader
                                            isLoading={payProcessing}
                                            onClick={handlePayNow}
                                            className="btn btn-success w-100 fw-bold"
                                        >
                                            {'Pay Now'.toTranslatedDisplay()}
                                        </ButtonWithLoader>
                                    )}
                                    <p className="text-muted text-center fs-8 mt-3 mb-0">{'Secured payment'.toTranslatedDisplay()}</p>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <CustomModal
                title={`Record Payment`}
                isVisible={isModalOpen}
                onClose={handleModalClose}
                body={<QuickPayForm onSave={handleModalClose} txnId={txnId} paymentType={txnConfig?.paymentType || PaymentType.CustomerPayment} onClose={handleModalClose} />}
                id={''}
                size="large"
                onSave={() => { }}
            />
            <CustomModal
                title={'Receive Advance Payment'.toTranslatedDisplay()}
                isVisible={isAdvanceModalOpen}
                onClose={() => setIsAdvanceModalOpen(false)}
                body={<QuickPayForm
                    paymentType={PaymentType.CustomerPayment}
                    advanceTxnId={txnId}
                    contactId={txn?.txnHeader?.contactId}
                    onSave={() => setIsAdvanceModalOpen(false)}
                    onClose={() => setIsAdvanceModalOpen(false)}
                />}
                id={'advance-modal'}
                size="large"
                onSave={() => { }}
            />
            <CustomModal
                title={'Enter Phone Number'.toTranslatedDisplay()}
                isVisible={isPhoneModalOpen}
                onClose={handlePhoneModalClose}
                body={
                    <div className="p-4">
                        <div className="mb-3">
                            <label className="form-label">{'WhatsApp Phone Number'.toTranslatedDisplay()}</label>
                            <input
                                type="tel"
                                className={`form-control ${phoneError ? 'is-invalid' : ''}`}
                                placeholder={'Enter phone number with country code'.toTranslatedDisplay()}
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(e.target.value);
                                    if (phoneError) setPhoneError('');
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handlePhoneSubmit();
                                    }
                                }}
                            />
                            {phoneError && <div className="invalid-feedback d-block">{phoneError}</div>}
                            <small className="form-text text-muted">
                                Include country code (e.g., +919876543210)
                            </small>
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handlePhoneModalClose}
                            >{'Cancel'.toTranslatedDisplay()}</button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handlePhoneSubmit}
                            >{'Send WhatsApp'.toTranslatedDisplay()}</button>
                        </div>
                    </div>
                }
                id="phone-number-modal"
                size="medium"
                onSave={handlePhoneSubmit}
            />
        </Content>
    );

}



export const handlePrint = async (htmlContent: string) => {
    if (htmlContent) {
        const printWindow = window.open('', '', 'height=600,width=800');
        if (printWindow) {
            printWindow.document.write(`
                        ${htmlContent}
            `);

            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.print();
                printWindow.onafterprint = () => {
                    printWindow.close();
                };
            };
        } else {
            console.error('Failed to open a new window. Please check if pop-ups are blocked.');
            alert('Unable to open the print window. Please disable pop-up blockers and try again.');
        }
    }
};
